import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { getISOTime } from '../../src/utils/functions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import {
  CanfarServiceType,
  ComputeServiceType,
  Project,
  RucioLocationsType,
  Site,
  SiteStorageArea,
} from '../utils/types';
import { ComputeJob } from '../components/compute-jobs/types';

export interface DataState {
  //
  projects: Project[];
  sites: Site[];
  computeJobs: ComputeJob[];
  computeJobsLoading: boolean;
  computeServices: ComputeServiceType[];
  canfarServices: CanfarServiceType[] | undefined;
  storageAreas: SiteStorageArea[];
  serviceTypes: string[];

  // a list of data items, and the Rucio locations
  // where those items are found, and where they are
  // being staged to.
  rucioLocations: RucioLocationsType[];
  //
}

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

const initialState: DataState = {
  projects: [],
  sites: [],
  computeJobs: [],
  computeJobsLoading: false,
  computeServices: [],
  canfarServices: undefined,
  storageAreas: [],
  serviceTypes: [],
  rucioLocations: [],
};

//	--------------------------------------------------------------------------
//
//	S L I C E S
//
//	--------------------------------------------------------------------------

export const dataSlice = createSlice({
  name: 'data',
  initialState: initialState,
  reducers: {
    //
    rD_setSites: (state: DataState, action: PayloadAction<Site[]>) => {
      state.sites = action.payload;
    },

    rD_setComputeJobs: (state: DataState, action: PayloadAction<ComputeJob[]>) => {
      state.computeJobs = action.payload;
    },

    rD_setComputeJobsLoading: (state: DataState, action: PayloadAction<boolean>) => {
      state.computeJobsLoading = action.payload;
    },

    rD_setComputeServices: (
      state: DataState,
      action: PayloadAction<ComputeServiceType[]>,
    ) => {
      state.computeServices = action.payload;
    },

    rD_setCanfarServices: (
      state: DataState,
      action: PayloadAction<CanfarServiceType[]>,
    ) => {
      state.canfarServices = action.payload;
    },

    rD_setProjects: (state: DataState, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
    },

    rD_setServiceTypes: (state: DataState, action: PayloadAction<string[]>) => {
      state.serviceTypes = action.payload;
    },

    rD_setStorageAreas: (state: DataState, action: PayloadAction<SiteStorageArea[]>) => {
      state.storageAreas = action.payload;
    },

    rD_updateCanfarImages: (
      state: DataState,
      action: PayloadAction<{
        id: string;
        images?: {
          id: string;
          types: string[];
        }[];
      }>,
    ) => {
      //
      // find the index.
      const index: number =
        state.canfarServices?.findIndex((service) => service.id === action.payload.id) ??
        -1;
      if (state.canfarServices !== undefined && index > -1) {
        //
        // update the images.
        let updateService: CanfarServiceType = state.canfarServices[index];
        updateService.images = action.payload.images;
        state.canfarServices = [
          ...state.canfarServices.slice(0, index),
          ...[updateService],
          ...state.canfarServices.slice(index + 1),
        ];
        //
      }
      //
    },

    rD_updateComputeServiceStatus: (
      state: DataState,
      action: PayloadAction<{
        id: string;
        activeStatus?: boolean;
        healthStatus?: string;
      }>,
    ) => {
      //
      // check if this service ID exists in the local compute state array.
      const index: number = state.computeServices.findIndex(
        (item) => item.compute_service_id === action.payload.id,
      );
      if (index > -1) {
        //
        // update the statuses from the SSE.
        let updateService: ComputeServiceType = state.computeServices[index];
        if (action.payload.activeStatus !== undefined) {
          updateService.active_status = action.payload.activeStatus;
        }
        if (action.payload.healthStatus !== undefined) {
          updateService.health_status = action.payload.healthStatus;
        }

        // update the state array with the new statuses.
        const updatedServices: ComputeServiceType[] = [
          ...state.computeServices.slice(0, index),
          ...[updateService],
          ...state.computeServices.slice(index + 1),
        ];
        state.computeServices = updatedServices;
        //
      }
      //
    },

    rD_updateRucioLocations: (
      state: DataState,
      action: PayloadAction<{
        items: {
          namespace: string;
          filename: string;
          refresh?: boolean | null;
          availableSites?: Site[];
          movingSites?: Site[];
          userCanAccessNamespace?: boolean;
          refreshingLocations?: boolean;
        }[];
        setTimestamp?: boolean;
        clearCatalog?: boolean;
        catalog?: string;
      }>,
    ) => {
      //
      // remove items from the list, and update existing items in the list.
      state.rucioLocations = [
        ...state.rucioLocations
          .filter(
            //
            // first, we need to filter out and remove any items that are in the catalog being updated, but don't exist in the payload.
            (item) => {
              const payloadIndex: number = action.payload.items.findIndex(
                (find) =>
                  item.namespace === find.namespace && item.filename === find.filename,
              );
              return (
                // KEEP the item if:
                action.payload.catalog === undefined || // we're updating ALL items, not just those in a specific catalog
                action.payload.clearCatalog !== true || // we don't want to clear the catalog
                payloadIndex > -1 || // the item is in the payload, so shouldn't be removed
                item.catalog.length > 1 || // the item is in multiple catalogs
                item.catalog.indexOf(action.payload.catalog) === -1 // the item is not in the catalog being cleared
              );
            },
            //
          )
          .map(
            //
            // next, we need to update all items in the payload, or return items that aren't in the payload.
            (item) => {
              const payloadIndex: number = action.payload.items.findIndex(
                (find) =>
                  item.namespace === find.namespace && item.filename === find.filename,
              );
              if (payloadIndex > -1) {
                //
                // update a record that exists on the payload.
                const newItem: RucioLocationsType = {
                  namespace: item.namespace,
                  filename: item.filename,
                  refresh:
                    action.payload.items[payloadIndex].refresh === null
                      ? undefined
                      : (action.payload.items[payloadIndex].refresh ?? item.refresh),
                  lastChecked:
                    action.payload.setTimestamp === true
                      ? getISOTime()
                      : item.lastChecked,
                  availableSites:
                    action.payload.items[payloadIndex].availableSites ??
                    item.availableSites,
                  movingSites:
                    action.payload.items[payloadIndex].movingSites ?? item.movingSites,
                  userCanAccessNamespace:
                    action.payload.items[payloadIndex].userCanAccessNamespace ??
                    item.userCanAccessNamespace,
                  refreshingLocations:
                    action.payload.items[payloadIndex].refreshingLocations ??
                    item.refreshingLocations,
                  catalog:
                    action.payload.catalog === undefined
                      ? item.catalog
                      : item.catalog.indexOf(action.payload.catalog) > -1
                        ? item.catalog
                        : [...item.catalog, ...[action.payload.catalog]],
                };
                return newItem;
                //
              } else {
                //
                // update a record that doesn't exist in the payload.
                if (action.payload.clearCatalog === true) {
                  //
                  // we're not removing the item because it exists in other catalogs. instead, remove the catalog from the catalog list.
                  const newItem: RucioLocationsType = Object.assign({}, item);
                  newItem.catalog = item.catalog.filter(
                    (filter) =>
                      action.payload.catalog === undefined ||
                      filter !== action.payload.catalog,
                  );
                  return newItem;
                  //
                } else {
                  //
                  // we are not clearing the catalog, and the item is not in the payload. we don't need to update it.
                  return item;
                  //
                }
                //
              }
            },
          ),

        // and add any payload items that don't already exist in the list.
        ...action.payload.items
          .filter(
            //
            // filter out any payload items that already exist.
            (item) => {
              const arrayIndex: number = state.rucioLocations.findIndex(
                (find) =>
                  item.namespace === find.namespace && item.filename === find.filename,
              );
              return arrayIndex === -1;
            },
            //
          )
          .map(
            //
            // and add any items that don't already exist.
            (item) => {
              const newItem: RucioLocationsType = {
                namespace: item.namespace,
                filename: item.filename,
                refresh: item.refresh === null ? undefined : item.refresh,
                lastChecked: getISOTime(),
                availableSites: item.availableSites ?? [],
                movingSites: item.movingSites ?? [],
                userCanAccessNamespace: item.userCanAccessNamespace,
                refreshingLocations: item.refreshingLocations ?? false,
                catalog: action.payload.catalog ? [action.payload.catalog] : [],
              };
              return newItem;
            },
          ),
      ];
      //
    },
    //
  },
});

// expose the interface.
export const {
  rD_setCanfarServices,
  rD_setComputeJobs,
  rD_setComputeJobsLoading,
  rD_setComputeServices,
  rD_setProjects,
  rD_setServiceTypes,
  rD_setSites,
  rD_setStorageAreas,
  rD_updateCanfarImages,
  rD_updateComputeServiceStatus,
  rD_updateRucioLocations,
} = dataSlice.actions;

export default dataSlice.reducer;
